import { cn } from "@lib/cn";
import type { TDesignSlide, TMasterSlide } from "@/shared/types";
import { CategoryItem } from "./sidebar-categories";

type TemplateCategoryGroup = {
  label: string;
  titles: string[];
};

export type OrderedSection = {
  key: string;
  categoryId: string;
  title: string;
  slides: TDesignSlide[];
};

const CHARTS_AND_DATA_ID = "charts-and-data";
const CHARTS_AND_DATA_TITLE = "Charts and Data";

export const TEMPLATE_CATEGORY_GROUPS: TemplateCategoryGroup[] = [
  {
    label: "Start",
    titles: [
      "Cover",
      "Welcome message",
      "Agenda",
      "Company in numbers",
      "Logo strip",
    ],
  },
  {
    label: "About",
    titles: [
      "About us",
      "Company statement",
      "Our mission",
      "Our vision",
      "Our values",
      "Team",
      "Leadership team",
      "Locations",
    ],
  },
  {
    label: "Solution",
    titles: [
      "Problem",
      "Solution overview",
      "Solutions",
      "Services",
      "What we do",
      "How it works",
      "Benefits",
      "Roadmap",
    ],
  },
  {
    label: "Proof",
    titles: [
      "Customers",
      "Case studies",
      "Testimonials",
      "KPIs",
      CHARTS_AND_DATA_TITLE,
      "Security and privacy",
    ],
  },
  {
    label: "Pricing",
    titles: ["Pricing table", "Pricing packages"],
  },
  {
    label: "Proposal & Agreement",
    titles: ["Accept button", "E-signature"],
  },
  {
    label: "Closing",
    titles: ["Last slide", "Footer"],
  },
];

const normalize = (s: string) => s.trim().toLowerCase();

export const buildTemplatesSections = (
  categories: TMasterSlide[],
  slidesMap: Record<string, TDesignSlide[]>,
): OrderedSection[] => {
  const byTitle = new Map<string, TMasterSlide>();
  for (const c of categories) byTitle.set(normalize(c.title), c);

  const sections: OrderedSection[] = [];
  const usedIds = new Set<string>();

  for (const group of TEMPLATE_CATEGORY_GROUPS) {
    for (const title of group.titles) {
      if (normalize(title) === normalize(CHARTS_AND_DATA_TITLE)) {
        const slides = slidesMap[CHARTS_AND_DATA_TITLE] ?? [];
        if (slides.length > 0) {
          sections.push({
            key: CHARTS_AND_DATA_ID,
            categoryId: CHARTS_AND_DATA_ID,
            title: CHARTS_AND_DATA_TITLE,
            slides,
          });
        }
        continue;
      }
      const cat = byTitle.get(normalize(title));
      if (!cat) continue;
      const slides = slidesMap[cat._id];
      if (!slides?.length) continue;
      usedIds.add(cat._id);
      sections.push({
        key: cat._id,
        categoryId: cat._id,
        title: cat.title,
        slides,
      });
    }
  }

  for (const cat of categories) {
    if (usedIds.has(cat._id)) continue;
    if (normalize(cat.title) === "most popular") continue;
    const slides = slidesMap[cat._id];
    if (!slides?.length) continue;
    sections.push({
      key: cat._id,
      categoryId: cat._id,
      title: cat.title,
      slides,
    });
  }

  return sections;
};

export const buildLayoutsSections = (
  categories: TMasterSlide[],
  slidesMap: Record<string, TDesignSlide[]>,
): OrderedSection[] => {
  const sections: OrderedSection[] = [];
  for (const cat of categories) {
    if (normalize(cat.title) === "most popular") continue;
    const slides = slidesMap[cat._id];
    if (!slides?.length) continue;
    sections.push({
      key: cat._id,
      categoryId: cat._id,
      title: cat.title,
      slides,
    });
  }
  return sections;
};

const SidebarGroupLabel = ({ label }: { label: string }) => (
  <div className="tw-flex tw-h-7 tw-w-full tw-flex-col tw-items-start tw-justify-center tw-px-2 tw-py-1.5">
    <span className="tw-font-sans tw-text-xs tw-font-normal tw-leading-4 tw-text-blue-500">
      {label}
    </span>
  </div>
);

export const TemplateCategorySidebarGroups = ({
  categories,
  onClick,
  isInSearch,
}: {
  categories: TMasterSlide[];
  onClick: (id: string) => void;
  isInSearch: boolean;
}) => {
  const byTitle = new Map<string, TMasterSlide>();
  for (const c of categories) byTitle.set(normalize(c.title), c);

  const usedIds = new Set<string>();
  const renderedGroups = TEMPLATE_CATEGORY_GROUPS.map((group) => {
    const items: { _id: string; title: string }[] = [];
    for (const title of group.titles) {
      if (normalize(title) === normalize(CHARTS_AND_DATA_TITLE)) {
        items.push({ _id: CHARTS_AND_DATA_ID, title: CHARTS_AND_DATA_TITLE });
        continue;
      }
      const match = byTitle.get(normalize(title));
      if (!match) continue;
      usedIds.add(match._id);
      items.push({ _id: match._id, title: match.title });
    }
    return { group, items };
  }).filter(({ items }) => items.length > 0);

  const orphans = categories.filter(
    (c) => !usedIds.has(c._id) && normalize(c.title) !== "most popular",
  );

  return (
    <>
      {renderedGroups.map(({ group, items }, idx) => (
        <div
          key={group.label}
          className={cn("tw-flex tw-flex-col tw-gap-1", idx > 0 && "tw-mt-2")}
        >
          <SidebarGroupLabel label={group.label} />
          {items.map((item) => (
            <CategoryItem
              key={item._id}
              _id={item._id}
              title={item.title}
              onClick={onClick}
              isInSearch={isInSearch}
              className="tw-px-2"
            />
          ))}
        </div>
      ))}
      {orphans.length > 0 && (
        <div className="tw-mt-2 tw-flex tw-flex-col tw-gap-1">
          {orphans.map((item) => (
            <CategoryItem
              key={item._id}
              _id={item._id}
              title={item.title}
              onClick={onClick}
              isInSearch={isInSearch}
              className="tw-px-2"
            />
          ))}
        </div>
      )}
    </>
  );
};
